import React, { useState } from 'react';
import { X, Loader2, ClipboardList } from 'lucide-react';
import { Appointment, HealthAssessment, MedicalCertificate } from '@/types';
import { api } from '@/services/api';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { createEmptyAssessment, getMissingFields } from '@/lib/healthAssessmentRequirements';
import { showAlertModal } from '@shared/components/common/AlertModal';

interface HealthAssessmentModalProps {
  appointment: Appointment;
  initialData?: HealthAssessment;
  drugTestOutcome?: MedicalCertificate['drugTestOutcome'];
  /**
   * Pre-selects the assessment shape for new assessments: 'screening' for
   * D&A-only bookings (derived from the service category), 'full' otherwise.
   * The technician can switch modes in the modal either way.
   */
  defaultAssessmentType?: 'full' | 'screening';
  onSaved: (healthAssessment: HealthAssessment) => void;
  onClose: () => void;
}

type OutcomeValue = 'acceptable' | 'refer';

// Predefined restriction clauses offered when the technician marks the patient
// "Fit with Restrictions". A technician may select any number of these.
const RESTRICTION_OPTIONS: string[] = [
  'Task assessment to be undertaken prior to undertaking tasks that require colour distinction',
  'Task assessment to ensure satisfactory visual requirement to undertake safety critical tasks',
  'Functional assessment required to determine whether audible instruction or warning signals can be heard',
  'Suitable and sufficient corrective visual appliances to be worn',
  'Suitable and efficient Hearing Protective Equipment should be used in the event of any noise hazard',
  'Functional assessment required in relation to mobility to undertake specific safety critical tasks',
  'Functional assessment to determine physical capacity to undertake task'
];

const RequiredMark = () => <span className="text-red-400 ml-0.5">*</span>;

// Inline caption shown beneath an empty mandatory field so it is unmistakable
// that the value is required (the amber tint alone is too easy to miss).
const RequiredHint = ({ show }: { show: boolean }) =>
  show ? (
    <span className="text-[9px] font-bold uppercase tracking-widest text-red-500">Required</span>
  ) : null;

const OutcomePair = ({
  label,
  value,
  onChange,
  required = false
}: {
  label: string;
  value: OutcomeValue | '';
  onChange: (v: OutcomeValue) => void;
  required?: boolean;
}) => (
  <div
    className={`flex items-center justify-between gap-4 py-2 border-b border-slate-100 last:border-0 rounded-lg px-2 -mx-2 transition-colors ${required && !value ? 'bg-amber-50/60' : ''}`}
  >
    <span className="text-xs font-semibold text-slate-700 flex flex-col">
      <span>
        {label}
        {required && <RequiredMark />}
      </span>
      <RequiredHint show={required && !value} />
    </span>
    <div className="flex gap-2">
      {(['acceptable', 'refer'] as OutcomeValue[]).map((opt) => (
        <label
          key={opt}
          className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest cursor-pointer border transition-all ${
            value === opt
              ? opt === 'acceptable'
                ? 'bg-green-50 border-green-400 text-green-700'
                : 'bg-red-50 border-red-400 text-red-700'
              : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
          }`}
        >
          <input
            type="radio"
            className="hidden"
            checked={value === opt}
            onChange={() => onChange(opt)}
          />
          {opt === 'acceptable' ? 'Acceptable' : 'Refer'}
        </label>
      ))}
    </div>
  </div>
);

const YesNo = ({
  label,
  value,
  onChange,
  required = false
}: {
  label: string;
  value: 'yes' | 'no' | '';
  onChange: (v: 'yes' | 'no') => void;
  required?: boolean;
}) => (
  <div
    className={`flex items-center justify-between gap-4 py-2 rounded-lg px-2 -mx-2 transition-colors ${required && !value ? 'bg-amber-50/60' : ''}`}
  >
    <span className="text-xs font-semibold text-slate-700 flex flex-col">
      <span>
        {label}
        {required && <RequiredMark />}
      </span>
      <RequiredHint show={required && !value} />
    </span>
    <div className="flex gap-2">
      {(['yes', 'no'] as const).map((opt) => (
        <label
          key={opt}
          className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest cursor-pointer border transition-all ${
            value === opt
              ? 'bg-teal-50 border-teal-400 text-teal-700'
              : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
          }`}
        >
          <input
            type="radio"
            className="hidden"
            checked={value === opt}
            onChange={() => onChange(opt)}
          />
          {opt === 'yes' ? 'Yes' : 'No'}
        </label>
      ))}
    </div>
  </div>
);

const TextInput = ({
  label,
  value,
  onChange,
  placeholder = '',
  required = false
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
}) => (
  <div className="flex flex-col gap-1">
    <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">
      {label}
      {required && <RequiredMark />}
    </label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`rounded-lg px-3 py-2 text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-400 bg-slate-50 border transition-colors ${required && !value ? 'border-red-300 bg-red-50/40' : 'border-slate-200'}`}
    />
    <RequiredHint show={required && !value} />
  </div>
);

// Numeric input: only allows digits, one decimal point, and (for blood pressure) one slash
const NumericInput = ({
  label,
  value,
  onChange,
  placeholder = '',
  required = false,
  pattern = 'decimal'
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  pattern?: 'decimal' | 'bloodpressure';
}) => {
  const handleChange = (raw: string) => {
    if (pattern === 'bloodpressure') {
      // Allow digits and a single slash, e.g. 120/80
      if (/^[\d/]*$/.test(raw) && (raw.match(/\//g) || []).length <= 1) onChange(raw);
    } else {
      // Allow digits and a single decimal point
      if (/^[\d.]*$/.test(raw) && (raw.match(/\./g) || []).length <= 1) onChange(raw);
    }
  };
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">
        {label}
        {required && <RequiredMark />}
      </label>
      <input
        type="text"
        inputMode={pattern === 'bloodpressure' ? 'numeric' : 'decimal'}
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        className={`rounded-lg px-3 py-2 text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-400 bg-slate-50 border transition-colors ${required && !value ? 'border-red-300 bg-red-50/40' : 'border-slate-200'}`}
      />
      <RequiredHint show={required && !value} />
    </div>
  );
};

const SectionHeader = ({ title }: { title: string }) => (
  <div className="bg-slate-100 px-4 py-2 rounded-lg mb-3">
    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{title}</p>
  </div>
);

export const HealthAssessmentModal: React.FC<HealthAssessmentModalProps> = ({
  appointment,
  initialData,
  drugTestOutcome,
  defaultAssessmentType = 'full',
  onSaved,
  onClose
}) => {
  const [data, setData] = useState<HealthAssessment>(
    initialData
      ? { assessmentType: 'full', ...initialData }
      : createEmptyAssessment(defaultAssessmentType)
  );
  const [isSaving, setIsSaving] = useState(false);
  const isScreening = data.assessmentType === 'screening';
  const hasDrugTest = Boolean(drugTestOutcome && drugTestOutcome !== 'not_tested');
  const showDrugTestBlock = isScreening || hasDrugTest;
  const missingFields = getMissingFields(data, hasDrugTest, isScreening);
  const complete = missingFields.length === 0;

  const set = <K extends keyof HealthAssessment>(key: K, value: HealthAssessment[K]) =>
    setData((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await api.saveHealthAssessment(appointment.id, data);
      onSaved(result.healthAssessment);
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to save health assessment.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[95] p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col animate-scale-up border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b bg-slate-50 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-teal-100 p-2.5 rounded-2xl text-teal-600">
              <ClipboardList className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-black text-lg text-slate-800 uppercase tracking-tight">
                {isScreening ? 'D&A Screening Record' : 'Health Assessment'}
              </h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                {appointment.customerName} — Section 10
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 bg-white p-2 rounded-full shadow-sm"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6 custom-scrollbar">
          {/* 0. Assessment type */}
          <div>
            <SectionHeader title="Assessment Type" />
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  ['full', 'Full Medical'],
                  ['screening', 'D&A Screening Only']
                ] as ['full' | 'screening', string][]
              ).map(([val, label]) => (
                <label
                  key={val}
                  className={`border-2 rounded-xl p-3 cursor-pointer flex items-center justify-center text-center transition-all ${
                    (data.assessmentType ?? 'full') === val
                      ? 'bg-teal-50 border-teal-500 text-teal-700'
                      : 'bg-white border-slate-100 text-slate-500 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    className="hidden"
                    checked={(data.assessmentType ?? 'full') === val}
                    onChange={() => set('assessmentType', val)}
                  />
                  <span className="font-black text-[10px] uppercase tracking-widest">{label}</span>
                </label>
              ))}
            </div>
            {isScreening && (
              <p className="mt-2 text-[10px] text-slate-500">
                D&A-only visit: medical exam sections are skipped; donor ID and drug & alcohol test
                results are required instead.
              </p>
            )}
          </div>

          {/* 1. Understanding */}
          <div>
            <SectionHeader title="Patient Understanding" />
            {(
              [
                ['reasonForAttending', 'Understands reason for attending'],
                ['whoYouAre', 'Understands who you are'],
                ['dataUsage', 'Understands what will happen to their data']
              ] as [keyof typeof data.understands, string][]
            ).map(([field, label]) => (
              <label
                key={field}
                className={`flex items-center gap-3 py-2 rounded-lg px-2 -mx-2 cursor-pointer transition-colors ${!data.understands[field] ? 'bg-amber-50/60' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={data.understands[field]}
                  onChange={(e) =>
                    set('understands', { ...data.understands, [field]: e.target.checked })
                  }
                  className="w-4 h-4 accent-teal-600"
                />
                <span className="text-xs font-semibold text-slate-700">
                  {label}
                  {!data.understands[field] && <RequiredMark />}
                </span>
              </label>
            ))}
          </div>

          {/* 2. Biometrics */}
          {!isScreening && (
            <>
              <div>
                <SectionHeader title="Biometrics" />
                <div className="grid grid-cols-3 gap-3">
                  <NumericInput
                    label="Height"
                    value={data.height}
                    onChange={(v) => set('height', v)}
                    placeholder="e.g. 180"
                    required
                  />
                  <NumericInput
                    label="Weight"
                    value={data.weight}
                    onChange={(v) => set('weight', v)}
                    placeholder="e.g. 80"
                    required
                  />
                  <NumericInput
                    label="BMI"
                    value={data.bmi}
                    onChange={(v) => set('bmi', v)}
                    placeholder="e.g. 24.7"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <NumericInput
                    label="Blood Pressure 1"
                    value={data.bloodPressure1}
                    onChange={(v) => set('bloodPressure1', v)}
                    placeholder="120/80"
                    required
                    pattern="bloodpressure"
                  />
                  <NumericInput
                    label="Blood Pressure 2"
                    value={data.bloodPressure2}
                    onChange={(v) => set('bloodPressure2', v)}
                    placeholder="118/78"
                    pattern="bloodpressure"
                  />
                  <NumericInput
                    label="Blood Pressure 3"
                    value={data.bloodPressure3}
                    onChange={(v) => set('bloodPressure3', v)}
                    placeholder="119/79"
                    pattern="bloodpressure"
                  />
                  <NumericInput
                    label="Pulse"
                    value={data.pulse}
                    onChange={(v) => set('pulse', v)}
                    placeholder="e.g. 72"
                    required
                  />
                </div>
              </div>

              {/* 3. Urinalysis */}
              <div>
                <SectionHeader title="Urinalysis" />
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  {(
                    [
                      ['nad', 'NAD'],
                      ['ketones', 'Ketones'],
                      ['protein', 'Protein'],
                      ['glucose', 'Glucose'],
                      ['blood', 'Blood']
                    ] as [keyof typeof data.urinalysis, string][]
                  ).map(([field, label]) => (
                    <label key={field} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={data.urinalysis[field] as boolean}
                        onChange={(e) =>
                          set('urinalysis', { ...data.urinalysis, [field]: e.target.checked })
                        }
                        className="w-4 h-4 accent-teal-600"
                      />
                      <span className="text-xs font-semibold text-slate-700">{label}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-3">
                  <NumericInput
                    label="pH"
                    value={data.urinalysis.ph}
                    onChange={(v) => set('urinalysis', { ...data.urinalysis, ph: v })}
                    placeholder="e.g. 6.5"
                  />
                </div>
              </div>
            </>
          )}

          {/* 4. Drugs & Alcohol */}
          <div>
            <SectionHeader title="Drugs & Alcohol" />
            <YesNo
              label="Uses Drugs"
              value={data.usesDrugs}
              onChange={(v) => set('usesDrugs', v)}
              required
            />
            <YesNo
              label="Drinks Alcohol"
              value={data.drinksAlcohol}
              onChange={(v) => set('drinksAlcohol', v)}
              required
            />

            {showDrugTestBlock && (
              <div className="mt-4 border border-slate-200 rounded-xl p-4 space-y-3 bg-slate-50">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  Donor ID
                </p>
                <YesNo
                  label="Donor ID Checked"
                  value={
                    data.donorIdChecked === true ? 'yes' : data.donorIdChecked === false ? 'no' : ''
                  }
                  onChange={(v) => set('donorIdChecked', v === 'yes')}
                  required={isScreening}
                />
                <TextInput
                  label="Method"
                  value={data.donorIdMethod || ''}
                  onChange={(v) => set('donorIdMethod', v)}
                  required={isScreening}
                />
                <TextInput
                  label="If no, ID verified by"
                  value={data.donorIdVerifiedBy || ''}
                  onChange={(v) => set('donorIdVerifiedBy', v)}
                />
                <div className="pt-2 border-t border-slate-200">
                  <YesNo
                    label="Drug Tested"
                    value={data.drugTested === true ? 'yes' : data.drugTested === false ? 'no' : ''}
                    onChange={(v) => set('drugTested', v === 'yes')}
                    required={isScreening}
                  />
                  <OutcomePair
                    label="Drug Test Outcome"
                    value={data.drugOutcome || ''}
                    onChange={(v) => set('drugOutcome', v)}
                    required={isScreening}
                  />
                  <YesNo
                    label="Alcohol Tested"
                    value={
                      data.alcoholTested === true ? 'yes' : data.alcoholTested === false ? 'no' : ''
                    }
                    onChange={(v) => set('alcoholTested', v === 'yes')}
                    required={isScreening}
                  />
                  <OutcomePair
                    label="Alcohol Test Outcome"
                    value={data.alcoholOutcome || ''}
                    onChange={(v) => set('alcoholOutcome', v)}
                    required={isScreening}
                  />
                </div>
              </div>
            )}
          </div>

          {/* 5. Hearing */}
          {!isScreening && (
            <>
              <div>
                <SectionHeader title="Hearing" />
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <TextInput
                    label="Otoscopy L"
                    value={data.hearing.otoscopyL}
                    onChange={(v) => set('hearing', { ...data.hearing, otoscopyL: v })}
                    required
                  />
                  <TextInput
                    label="Otoscopy R"
                    value={data.hearing.otoscopyR}
                    onChange={(v) => set('hearing', { ...data.hearing, otoscopyR: v })}
                    required
                  />
                  <TextInput
                    label="Category"
                    value={data.hearing.category}
                    onChange={(v) => set('hearing', { ...data.hearing, category: v })}
                    required
                  />
                </div>
                <OutcomePair
                  label="Hearing"
                  value={data.hearing.outcome}
                  onChange={(v) => set('hearing', { ...data.hearing, outcome: v })}
                  required
                />
              </div>

              {/* 6. Visual Acuity */}
              <div>
                <SectionHeader title="Visual Acuity" />
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <TextInput
                    label="Snellens Without Glasses L"
                    value={data.visualAcuity.snellensWithoutL}
                    onChange={(v) =>
                      set('visualAcuity', { ...data.visualAcuity, snellensWithoutL: v })
                    }
                    placeholder="6/"
                  />
                  <TextInput
                    label="Snellens Without Glasses R"
                    value={data.visualAcuity.snellensWithoutR}
                    onChange={(v) =>
                      set('visualAcuity', { ...data.visualAcuity, snellensWithoutR: v })
                    }
                    placeholder="6/"
                  />
                  <TextInput
                    label="Snellens Corrective L"
                    value={data.visualAcuity.snellensCorrectiveL}
                    onChange={(v) =>
                      set('visualAcuity', { ...data.visualAcuity, snellensCorrectiveL: v })
                    }
                    placeholder="6/"
                  />
                  <TextInput
                    label="Snellens Corrective R"
                    value={data.visualAcuity.snellensCorrectiveR}
                    onChange={(v) =>
                      set('visualAcuity', { ...data.visualAcuity, snellensCorrectiveR: v })
                    }
                    placeholder="6/"
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={data.visualAcuity.peripheralMeetsSom}
                    onChange={(e) =>
                      set('visualAcuity', {
                        ...data.visualAcuity,
                        peripheralMeetsSom: e.target.checked
                      })
                    }
                    className="w-4 h-4 accent-teal-600"
                  />
                  <span className="text-xs font-semibold text-slate-700">
                    Peripheral vision meets SOM Standards
                  </span>
                </label>
                <OutcomePair
                  label="Visual Acuity"
                  value={data.visualAcuity.outcome}
                  onChange={(v) => set('visualAcuity', { ...data.visualAcuity, outcome: v })}
                  required
                />
                <OutcomePair
                  label="Colour Perception"
                  value={data.colourPerception}
                  onChange={(v) => set('colourPerception', v)}
                  required
                />
              </div>

              {/* 7. Lung Function + MSD */}
              <div>
                <SectionHeader title="Lung Function & Mobility" />
                <OutcomePair
                  label="Lung Function"
                  value={data.lungFunction}
                  onChange={(v) => set('lungFunction', v)}
                  required
                />
                <OutcomePair
                  label="MSD's / Mobility"
                  value={data.msdMobility}
                  onChange={(v) => set('msdMobility', v)}
                  required
                />
              </div>

              {/* 8. Mental Health */}
              <div>
                <SectionHeader title="Mental Health" />
                <div className="mb-3">
                  <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-1">
                    Comment
                  </label>
                  <textarea
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-400 bg-slate-50 resize-none"
                    rows={2}
                    value={data.mentalHealth.comment}
                    onChange={(e) =>
                      set('mentalHealth', { ...data.mentalHealth, comment: e.target.value })
                    }
                  />
                </div>
                <OutcomePair
                  label="Mental Health"
                  value={data.mentalHealth.outcome}
                  onChange={(v) => set('mentalHealth', { ...data.mentalHealth, outcome: v })}
                  required
                />
              </div>

              {/* 9. General Health */}
              <div>
                <SectionHeader title="General Health" />
                <OutcomePair
                  label="General Health"
                  value={data.generalHealth.outcome}
                  onChange={(v) => set('generalHealth', { ...data.generalHealth, outcome: v })}
                  required
                />
                <div className="mt-2">
                  <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-1">
                    Details
                  </label>
                  <textarea
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-400 bg-slate-50 resize-none"
                    rows={2}
                    value={data.generalHealth.details}
                    onChange={(e) =>
                      set('generalHealth', { ...data.generalHealth, details: e.target.value })
                    }
                  />
                </div>
              </div>
            </>
          )}

          {/* 10. Medication */}
          <div>
            <SectionHeader title="Medication" />
            <YesNo
              label="Medication"
              value={data.medication.hasMedication ? 'yes' : 'no'}
              onChange={(v) =>
                set('medication', { ...data.medication, hasMedication: v === 'yes' })
              }
            />
            {data.medication.hasMedication && (
              <div className="mt-2">
                <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 block mb-1">
                  Details
                </label>
                <textarea
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-400 bg-slate-50 resize-none"
                  rows={2}
                  value={data.medication.details}
                  onChange={(e) =>
                    set('medication', { ...data.medication, details: e.target.value })
                  }
                />
              </div>
            )}
          </div>

          {/* 11. Fitness Determination */}
          <div>
            <SectionHeader title="Fitness Determination" />
            {!data.fitStatus && (
              <p className="text-[10px] text-amber-600 font-bold mb-2 flex items-center gap-1">
                <span className="text-red-400">*</span> Selection required
              </p>
            )}
            <div
              className={`grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4 rounded-xl p-1 transition-colors ${!data.fitStatus ? 'bg-amber-50/50' : ''}`}
            >
              {(
                [
                  ['fit', 'Fit'],
                  ['fit_restrictions', 'Fit with Restrictions'],
                  ['refer', 'Refer'],
                  ['temporarily_unfit', 'Temporarily Unfit']
                ] as [HealthAssessment['fitStatus'], string][]
              ).map(([val, label]) => (
                <label
                  key={val}
                  className={`border-2 rounded-xl p-3 cursor-pointer flex items-center justify-center text-center transition-all ${
                    data.fitStatus === val
                      ? 'bg-teal-50 border-teal-500 text-teal-700'
                      : 'bg-white border-slate-100 text-slate-500 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    className="hidden"
                    checked={data.fitStatus === val}
                    onChange={() => set('fitStatus', val)}
                  />
                  <span className="font-black text-[10px] uppercase tracking-widest">{label}</span>
                </label>
              ))}
            </div>
            {data.fitStatus === 'fit_restrictions' && (
              <div className="mb-4 border border-slate-200 rounded-xl p-4 bg-slate-50">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">
                  Restrictions
                </p>
                <div className="space-y-2">
                  {RESTRICTION_OPTIONS.map((option) => {
                    const selected = (data.restrictions || []).includes(option);
                    return (
                      <label
                        key={option}
                        className="flex items-start gap-2 cursor-pointer text-xs font-semibold text-slate-700"
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) =>
                            set(
                              'restrictions',
                              e.target.checked
                                ? [...(data.restrictions || []), option]
                                : (data.restrictions || []).filter((r) => r !== option)
                            )
                          }
                          className="w-4 h-4 mt-0.5 accent-teal-600 shrink-0"
                        />
                        <span>{option}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            <TextInput
              label="Recall"
              value={data.recall}
              onChange={(v) => set('recall', v)}
              placeholder="e.g. 6 months"
            />
          </div>

          {/* 12. Clinical Notes */}
          <div>
            <SectionHeader title="Clinical Notes" />
            <textarea
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-400 bg-slate-50 resize-none"
              rows={4}
              value={data.clinicalNotes}
              onChange={(e) => set('clinicalNotes', e.target.value)}
              placeholder="Comment / Clinical Notes..."
            />
            <label className="flex items-center gap-2 cursor-pointer mt-3">
              <input
                type="checkbox"
                checked={data.continuedOverleaf}
                onChange={(e) => set('continuedOverleaf', e.target.checked)}
                className="w-4 h-4 accent-teal-600"
              />
              <span className="text-xs font-semibold text-slate-700">Continued overleaf</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-slate-50 flex items-center justify-between gap-3 shrink-0">
          <p
            className={`text-[10px] font-bold uppercase tracking-widest ${complete ? 'text-green-600' : 'text-red-500'}`}
          >
            {complete
              ? 'All required fields complete'
              : `${missingFields.length} required field${missingFields.length === 1 ? '' : 's'} remaining`}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-6 py-3 text-slate-400 font-bold uppercase text-[10px] tracking-widest"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!complete || isSaving}
              title={complete ? undefined : `Missing: ${missingFields.join(', ')}`}
              className="bg-teal-600 text-white px-8 py-3 rounded-2xl font-black hover:bg-teal-700 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed shadow-xl shadow-teal-600/20 active:scale-95 transition-all uppercase tracking-widest text-[11px]"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ClipboardList className="w-4 h-4" />
              )}
              Save Assessment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
